/* eslint-disable no-null/no-null */
import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateType,
  ISO_DATE_FORMAT,
  Timestamp,
  Timezone,
  TypeGuard,
  toTimestamp,
} from '@principle-theorem/shared';
import { flow, isBoolean, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE } from '../../../destination/entities/consts';
import { EXISTING_PATIENT_DESTINATION_ENTITY } from '../../../destination/entities/existing-patient';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PATIENT_APPOINTMENT_RESOURCE_TYPE } from '../../../destination/entities/patient-appointments';
import { PATIENT_INTERACTION_RESOURCE_TYPE } from '../../../destination/entities/patient-interactions';
import { PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE } from '../../../destination/entities/patient-treatment-plans';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { PATIENT_CLINICAL_CHART_DESTINATION_ENTITY } from '../../destination/entities/patient-clinical-charts';
import { PATIENT_DOCUMENTS_RESOURCE_TYPE } from '../../destination/entities/patient-documents';
import { PATIENT_INVOICE_DESTINATION_ENTITY } from '../../destination/entities/patient-invoices';
import { PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE } from '../../destination/entities/patient-medical-history-documents';
import { PATIENT_RELATIONSHIP_RESOURCE_ENTITY } from '../../destination/entities/patient-relationships';
import { PATIENT_XRAYS_RESOURCE_TYPE } from '../../destination/entities/patient-xrays';
import { EXACT_DATE_TIME_FORMAT, convertExactId } from '../../util/helpers';
import { PATIENT_INVOICE_RESOURCE_TYPE_V2 } from '../../destination/entities/patient-invoices-v2';

export const PATIENT_SOURCE_ENTITY: ISourceEntity = SourceEntity.init({
  metadata: {
    label: 'Patient List',
    description: `
    "previousname" is mostly being used to store random patient info like Mum or dads name.
    "alsoknownas" is often used to store the medicare card number and position.
    "occupation" is also used to store the medicare card number and position.
    All 3 can be expected to store any of that kind of data.
    Also, the patient data is missing any "created_date" so we can't use that to filter.
    `,
    idPrefix: PATIENT_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export enum ExactSex {
  Male = 'Male',
  Female = 'Female',
}

export interface IExactPatient {
  patient_id: string;
  is_deleted: boolean;
  head_of_family_code: string | null;
  initials: string | null;
  first_name: string | null;
  last_name: string | null;
  second_name: string | null;
  title: string | null;
  sex: ExactSex | null;
  date_of_birth: string | null;
  medicare_card_number: string | null;
  medicare_card_position: string | null;
  medicare_expiry_date: string | null;
  home_address_1: string | null;
  home_address_2: string | null;
  home_address_suburb: string | null;
  home_address_town_city: string | null;
  home_address_postcode: string | null;
  home_address_state: string | null;
  home_phone: string | null;
  mobile_number: string | null;
  work_phone: string | null;
  email: string | null;
  contact_preference: string | null;
  preferred_practitioner_initials: string | null;
  preferred_hygienist_initials: string | null;
  referrer_id: string | null;
  referral_source_id: string | null;
  family_id: string | null;
  is_head_of_family: boolean;
  first_visit_datetime: string | null;
  last_visit_datetime: string | null;
  patient_note: string | null;
  healthfund_code: string | null;
  healthfund_patient_id: string | null;
  preferred_fee_schedule: string | null;
  referral_patient_id: string | null;
  location_id: string | null;
  is_inactive: boolean | null;
  patient_created_datetime: string | null;
  id_number: string | null; // This can be used for NZ NHI number
  community_service_cardnum: string | null;
  community_service_cardexpiry: string | null;
}

function isExactPatient(
  item: unknown,
  logError: boolean
): item is IExactPatient {
  return TypeGuard.interface<IExactPatient>(
    {
      patient_id: isString,
      is_deleted: isBoolean,
      head_of_family_code: TypeGuard.nilOr(isString),
      initials: TypeGuard.nilOr(isString),
      first_name: TypeGuard.nilOr(isString),
      last_name: TypeGuard.nilOr(isString),
      second_name: TypeGuard.nilOr(isString),
      title: TypeGuard.nilOr(isString),
      sex: TypeGuard.nilOr(TypeGuard.enumValue(ExactSex)),
      date_of_birth: TypeGuard.nilOr(isString),
      medicare_card_number: TypeGuard.nilOr(isString),
      medicare_card_position: TypeGuard.nilOr(isString),
      medicare_expiry_date: TypeGuard.nilOr(isString),
      home_address_1: TypeGuard.nilOr(isString),
      home_address_2: TypeGuard.nilOr(isString),
      home_address_suburb: TypeGuard.nilOr(isString),
      home_address_town_city: TypeGuard.nilOr(isString),
      home_address_postcode: TypeGuard.nilOr(isString),
      home_address_state: TypeGuard.nilOr(isString),
      home_phone: TypeGuard.nilOr(isString),
      mobile_number: TypeGuard.nilOr(isString),
      work_phone: TypeGuard.nilOr(isString),
      email: TypeGuard.nilOr(isString),
      contact_preference: TypeGuard.nilOr(isString),
      preferred_practitioner_initials: TypeGuard.nilOr(isString),
      preferred_hygienist_initials: TypeGuard.nilOr(isString),
      referrer_id: TypeGuard.nilOr(isString),
      referral_source_id: TypeGuard.nilOr(isString),
      family_id: TypeGuard.nilOr(isString),
      is_head_of_family: isBoolean,
      first_visit_datetime: TypeGuard.nilOr(isString),
      last_visit_datetime: TypeGuard.nilOr(isString),
      patient_note: TypeGuard.nilOr(isString),
      healthfund_code: TypeGuard.nilOr(isString),
      healthfund_patient_id: TypeGuard.nilOr(isString),
      preferred_fee_schedule: TypeGuard.nilOr(isString),
      referral_patient_id: TypeGuard.nilOr(isString),
      location_id: TypeGuard.nilOr(isString),
      is_inactive: TypeGuard.nilOr(isBoolean),
      patient_created_datetime: TypeGuard.nilOr(isString),
      id_number: TypeGuard.nilOr(isString),
      community_service_cardnum: TypeGuard.nilOr(isString),
      community_service_cardexpiry: TypeGuard.nilOr(isString),
    },
    logError
  )(item);
}

export interface IExactPatientTranslations {
  dateOfBirth?: ISODateType;
  firstVisitDatetime?: Timestamp;
  lastVisitDatetime?: Timestamp;
  medicareExpiryDate?: Timestamp;
  createdDate?: Timestamp;
}

export interface IExactPatientFilters {
  patientId: string;
  familyId?: string;
}

const PATIENT_SOURCE_QUERY = `
SELECT
  patient_details.*,
  patient_email.*
FROM (
  SELECT
    patientid::TEXT AS patient_id,
    NULLIF(headoffamilycode, '') AS head_of_family_code,
    NULLIF(initials, '') AS initials,
    NULLIF(firstname, '') AS first_name,
    NULLIF(lastname, '') AS last_name,
    NULLIF(secondname, '') AS second_name,
    NULLIF(title, '') AS title,
    NULLIF(sex, '') AS sex,
    NULLIF(dateofbirth, '') AS date_of_birth,
    NULLIF(medicarecardnumber::TEXT, '') AS medicare_card_number,
    NULLIF(medicareirn::TEXT, '') AS medicare_card_position,
    NULLIF(medicareexpirydate, '') AS medicare_expiry_date,
    NULLIF(convaddress_homeaddress_address1, '') AS home_address_1,
    NULLIF(convaddress_homeaddress_address2, '') AS home_address_2,
    NULLIF(convaddress_homeaddress_suburb, '') AS home_address_suburb,
    NULLIF(convaddress_homeaddress_towncity, '') AS home_address_town_city,
    NULLIF(convaddress_homeaddress_postcodezipcode::TEXT, '') AS home_address_postcode,
    NULLIF(convaddress_homeaddress_areastateprovince::TEXT, '') AS home_address_state,
    NULLIF(homephone::TEXT, '') AS home_phone,
    NULLIF(mobile::TEXT, '') AS mobile_number,
    NULLIF(workphone::TEXT, '') AS work_phone,
    contactpreference AS contact_preference,
    NULLIF(dentist::TEXT, '') AS preferred_practitioner_initials,
    NULLIF(hygienist, '') AS preferred_hygienist_initials,
    NULLIF(referrerid, '') AS referrer_id,
    NULLIF(referrerotherid::TEXT, '') AS referral_source_id,
    NULLIF(familyid::TEXT, '') AS family_id,
    isheadoffamily AS is_head_of_family,
    firstvisitdatetime AS first_visit_datetime,
    lastvisitdatetime AS last_visit_datetime,
    NULLIF(patientnote, '') AS patient_note,
    NULLIF(healthfundcode, '') AS healthfund_code,
    NULLIF(healthfundpatientid::TEXT, '') AS healthfund_patient_id,
    NULLIF(payor, '') AS preferred_fee_schedule,
    NULLIF(referralpatient::TEXT, '') AS referral_patient_id,
    NULLIF(locationid::TEXT, '') AS location_id,
    isinactive AS is_inactive,
    NULLIF(patientcreateddatetime, '') AS patient_created_datetime,
    NULLIF(idnumber, '') AS id_number,
    NULLIF(communityservicecardnum::TEXT, '') AS community_service_cardnum,
    NULLIF(communityservicecardexpiry, '') AS community_service_cardexpiry
  FROM
    convpatient
) AS patient_details
LEFT JOIN (
  SELECT
    patientid::TEXT,
    emailaddress AS email
  FROM
    convpatientemail
) AS patient_email
ON patient_details.patient_id::TEXT = patient_email.patientid::TEXT
WHERE patient_details.first_name != '' OR patient_details.last_name != ''
`;

export type ExactPatient = ISourceEntityTypes<
  IExactPatient,
  IExactPatientTranslations,
  IExactPatientFilters
>;

export class PatientSourceEntity extends BaseSourceEntity<ExactPatient> {
  sourceEntity = PATIENT_SOURCE_ENTITY;
  entityResourceType = PATIENT_RESOURCE_TYPE;
  sourceQuery = PATIENT_SOURCE_QUERY;
  verifySourceFn = isExactPatient;
  override transformDataFn = flow([transformPatientResults]);

  migrationDestinations = [
    PATIENT_CLINICAL_CHART_DESTINATION_ENTITY.metadata.key,
    PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE,
    PATIENT_DOCUMENTS_RESOURCE_TYPE,
    PATIENT_INTERACTION_RESOURCE_TYPE,
    PATIENT_INVOICE_DESTINATION_ENTITY.metadata.key,
    PATIENT_INVOICE_RESOURCE_TYPE_V2,
    PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE,
    PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE,
    PATIENT_XRAYS_RESOURCE_TYPE,
    PATIENT_RESOURCE_TYPE,
    PATIENT_APPOINTMENT_RESOURCE_TYPE,
    EXISTING_PATIENT_DESTINATION_ENTITY.metadata.key,
    PATIENT_RELATIONSHIP_RESOURCE_ENTITY.metadata.key,
  ];

  translate(
    patient: IExactPatient,
    timezone: Timezone
  ): IExactPatientTranslations {
    return {
      dateOfBirth: patient.date_of_birth
        ? moment
            .tz(patient.date_of_birth, EXACT_DATE_TIME_FORMAT, timezone)
            .format(ISO_DATE_FORMAT)
        : undefined,
      firstVisitDatetime: patient.first_visit_datetime
        ? toTimestamp(
            moment.tz(
              patient.first_visit_datetime,
              EXACT_DATE_TIME_FORMAT,
              timezone
            )
          )
        : undefined,
      lastVisitDatetime: patient.last_visit_datetime
        ? toTimestamp(
            moment.tz(
              patient.last_visit_datetime,
              EXACT_DATE_TIME_FORMAT,
              timezone
            )
          )
        : undefined,
      medicareExpiryDate: patient.medicare_expiry_date
        ? toTimestamp(
            moment.tz(
              patient.medicare_expiry_date,
              EXACT_DATE_TIME_FORMAT,
              timezone
            )
          )
        : undefined,
      createdDate: patient.patient_created_datetime
        ? toTimestamp(
            moment.tz(
              patient.patient_created_datetime,
              EXACT_DATE_TIME_FORMAT,
              timezone
            )
          )
        : undefined,
    };
  }

  getFilterData(patient: IExactPatient): IExactPatientFilters {
    return {
      patientId: patient.patient_id,
      familyId: patient.family_id ?? undefined,
    };
  }

  getSourceRecordId(data: IExactPatient): string {
    return data.patient_id;
  }

  getSourceLabel(data: IExactPatient): string {
    return `${data.patient_id} ${data.first_name ?? ''} ${
      data.last_name ?? ''
    }`;
  }
}

function transformPatientResults(rows: IExactPatient[]): IExactPatient[] {
  return rows.map((exactPatient) => ({
    ...exactPatient,
    is_deleted: exactPatient.last_name?.indexOf('*^Deleted') !== -1,
    patient_id: convertExactId(exactPatient.patient_id),
    referral_patient_id: exactPatient.referral_patient_id
      ? convertExactId(exactPatient.referral_patient_id)
      : null,
    referrer_id: exactPatient.referrer_id
      ? convertExactId(exactPatient.referrer_id)
      : null,
    referral_source_id: exactPatient.referral_source_id
      ? convertExactId(exactPatient.referral_source_id)
      : null,
    family_id: exactPatient.family_id
      ? convertExactId(exactPatient.family_id)
      : null,
  }));
}
